import { useCallback, useMemo } from 'react';

import { RadioOptionsWithConfirm } from 'shared-components';
import { TravelStatusEnum } from 'types';

import { AdditionQuestionsEnum } from './types';
import { IQuestion, ITravelStatus } from '../../../../../../../types';

interface IAnswer {
  status: TravelStatusEnum | AdditionQuestionsEnum;
  label: string;
  isSelected?: () => boolean;
}
interface IProps {
  travelStatus: ITravelStatus;
  selectedStatus: {
    status: ITravelStatus['status'] | AdditionQuestionsEnum;
    setStatus: (status: TravelStatusEnum | AdditionQuestionsEnum | undefined) => void;
  };
}

export const WhatIsYourTravelStatus = ({ selectedStatus, travelStatus }: IProps) => {
  const { status, setStatus } = selectedStatus;

  const tStatus = travelStatus.status || TravelStatusEnum.None;

  const notHandledQuestions = useMemo(() => getArrayOfQuestions(tStatus!), [tStatus]);

  const getTravelStatusFromLabel = useCallback(
    (label: string) => {
      return notHandledQuestions.find(notHandledQuestion => {
        return notHandledQuestion.label === label;
      })!.status;
    },
    [notHandledQuestions]
  );

  const questions = useMemo(
    () =>
      notHandledQuestions.map((answer: IAnswer, index: number) => {
        return {
          char: (index + 1).toString(),
          label: answer.label,
          selected: answer.isSelected ? answer.isSelected() : status === getTravelStatusFromLabel(answer.label)
        };
      }),
    [getTravelStatusFromLabel, notHandledQuestions, status]
  );

  const clickHandler = useCallback(
    (question: IQuestion) => {
      const newStatus = getTravelStatusFromLabel(question.label);
      setStatus(newStatus);
      return true;
    },
    [getTravelStatusFromLabel, setStatus]
  );

  return (
    <>
      <RadioOptionsWithConfirm
        label={
          tStatus === TravelStatusEnum.None ? `What is your current travel status?` : 'How has your travel changed?'
        }
        questions={questions}
        onClick={clickHandler}
      />
    </>
  );
};

const getArrayOfQuestions = (currentStatus: TravelStatusEnum): IAnswer[] => {
  const defaultStatuses: IAnswer[] = [
    {
      status: TravelStatusEnum.TravelCancelled,
      label: 'My travel has been cancelled'
    },
    {
      status: TravelStatusEnum.TravelPrevented,
      label: 'Something is preventing my travel'
    },
    {
      status: TravelStatusEnum.Defer,
      label: 'I intend to defer to a later intake'
    },
    {
      status: TravelStatusEnum.Withdraw,
      label: 'I intend to withdraw my application'
    }
  ];

  const getAdditionalAnswers = (): IAnswer[] => {
    switch (currentStatus) {
      case TravelStatusEnum.None: {
        return [
          {
            status: TravelStatusEnum.Booked,
            label: 'I have made a travel booking'
          },
          {
            status: TravelStatusEnum.NoBookingYet,
            label: 'I have not made a travel booking'
          },
          {
            status: TravelStatusEnum.AlreadyInUk,
            label: 'I am already in the UK'
          }
        ];
      }

      case TravelStatusEnum.Booked: {
        return [
          {
            status: TravelStatusEnum.Arrived,
            label: 'I have arrived in the UK'
          },
          {
            status: AdditionQuestionsEnum.NewArrivedDate,
            label: 'I have a new booking or arrival date'
          }
        ];
      }
      case TravelStatusEnum.Arrived: {
        return [
          {
            status: TravelStatusEnum.AlreadyInUk,
            label: 'I am already in the UK'
          }
        ];
      }

      default: {
        return [
          {
            status: TravelStatusEnum.Booked,
            label: 'I have made a travel booking'
          },
          {
            status: TravelStatusEnum.NoBookingYet,
            label: 'I have not made a travel booking'
          }
        ];
      }
    }
  };

  return [...getAdditionalAnswers(), ...defaultStatuses].filter(({ status }) => status !== currentStatus);
};
